import { Toggle, Tooltip, TooltipContent, TooltipTrigger } from "@pdfking/components"

import { useTranslation } from "../i18n/useTranslation"
import { TOGGLE_BUTTON_CLASS } from "./ToggleButtonClass"
import { DEFAULT_TOGGLE_BUTTON_ICON, TOGGLE_BUTTON_ICONS } from "./ToggleButtonIcons"

/**
 * Icon-only pressed/unpressed button for a named view toggle (BLEED, a
 * guide's destination name, ...) — the icon comes from
 * {@link TOGGLE_BUTTON_ICONS}, the label from a tooltip. Used for both the
 * BLEED toggle and each guide toggle, so they read as one control family.
 */
export function ToggleButton({
  className,
  name,
  pressed,
  onPressedChange,
  // Looked up from pdfview's own translations (`entities`, then
  // `imposition`) by default — pass this to source labels from a different
  // library when embedding the button outside pdfview.
  translate,
}: {
  className?: string
  name: string
  pressed: boolean
  onPressedChange: () => void
  translate?: (name: string) => string
}) {
  const entities = useTranslation("entities")
  const imposition = useTranslation("imposition")
  const label = translate ? translate(name) : (entities[name]?.() ?? imposition[name]?.() ?? name)
  const Icon =
    (TOGGLE_BUTTON_ICONS as Record<string, typeof DEFAULT_TOGGLE_BUTTON_ICON>)[name] ??
    DEFAULT_TOGGLE_BUTTON_ICON

  return (
    <Tooltip>
      {/* `asChild` needs a real DOM node for ref-forwarding — `Toggle` isn't
          forwardRef'd, so it wraps in a plain `span` here, same as
          RangeControl's Tooltip+Button pairs. */}
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Toggle
            aria-label={label}
            className={TOGGLE_BUTTON_CLASS}
            onPressedChange={onPressedChange}
            pressed={pressed}
            size="sm"
          >
            <Icon className={className} />
          </Toggle>
        </span>
      </TooltipTrigger>
      {/* Downwards, not Radix's default upwards: every one of these lives in a
          header (see ViewerHeader), where "above the button" is off the top of
          the viewport in the 2D viewer and over the window's own titlebar in
          the desktop builds. Same default TooltipIconButton already carries,
          for the same reason. */}
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  )
}
